import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  FileText,
  Plus,
  CheckCircle,
  X
} from 'lucide-react'

interface Contrato {
  id: string
  numero_contrato: string
  tipo_contrato: 'principal' | 'adicional' | 'renovacao' | 'upgrade' | 'projeto' | 'aditivo'
  status_relacionamento: 'independente' | 'vinculado' | 'substitui' | 'complementa'
  plano_ids: string[]
  conteudo_original: string
  conteudo_editado: string
  dados_variaveis: any
  data_inicio?: string
  data_fim?: string
  status: string
  observacoes?: string
  arquivo_url?: string
  created_at: string
  updated_at: string
  contrato_pai?: {
    numero_contrato: string
    tipo_contrato: string
  }
  contrato_substitui?: {
    numero_contrato: string
    tipo_contrato: string
  }
}

interface Plano {
  id: string
  nome: string
  valor_base: number
  status: string
}

interface ModalContratosProps {
  isOpen: boolean
  onClose: () => void
  clienteId: string
  planos: Plano[]
  onContractCreated: (contrato: Contrato) => void
  onEditContract: (contrato: Contrato) => void
}

const ModalContratos: React.FC<ModalContratosProps> = ({
  isOpen,
  onClose,
  clienteId,
  planos,
  onContractCreated,
  onEditContract
}) => {
  const [step, setStep] = useState<'form' | 'success'>('form')
  const [contratoGerado, setContratoGerado] = useState<Contrato | null>(null)
  const [formData, setFormData] = useState({
    tipo_contrato: 'principal' as const,
    plano_ids: [] as string[],
    observacoes: ''
  })

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      setStep('form')
      setContratoGerado(null)
      setFormData({
        tipo_contrato: 'principal',
        plano_ids: [],
        observacoes: ''
      })
    }
  }, [isOpen])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const handleGerarContrato = async () => {
    if (formData.plano_ids.length === 0) {
      alert('Selecione pelo menos um plano')
      return
    }

    try {
      const response = await fetch('/api/contratos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cliente_id: clienteId,
          ...formData
        })
      })

      const result = await response.json()

      if (response.ok) {
        setContratoGerado(result.data)
        setStep('success')
        onContractCreated(result.data)
      } else {
        alert('Erro ao gerar contrato: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao gerar contrato:', error)
      alert('Erro ao gerar contrato')
    }
  }

  const handleEditarAgora = () => {
    if (contratoGerado) {
      onEditContract(contratoGerado)
      onClose()
    }
  }

  const handleClose = () => {
    setStep('form')
    setContratoGerado(null)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[70] max-w-md w-full mx-4">
      <div className="relative bg-white rounded-lg shadow-2xl border-2 border-gray-200" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {step === 'form' ? 'Novo Contrato' : 'Contrato Criado!'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'form' ? (
            <div className="space-y-4">
              {/* Tipo de Contrato */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Contrato
                </label>
                <select
                  value={formData.tipo_contrato}
                  onChange={(e) => setFormData({...formData, tipo_contrato: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="principal">Principal</option>
                  <option value="adicional">Serviços Adicionais</option>
                  <option value="renovacao">Renovação</option>
                  <option value="projeto">Projeto Específico</option>
                </select>
              </div>

              {/* Planos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Planos a Incluir
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                  {planos.map((plano) => (
                    <label key={plano.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.plano_ids.includes(plano.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              plano_ids: [...formData.plano_ids, plano.id]
                            })
                          } else {
                            setFormData({
                              ...formData,
                              plano_ids: formData.plano_ids.filter(id => id !== plano.id)
                            })
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        {plano.nome} - {formatCurrency(plano.valor_base)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Contrato Gerado com Sucesso!</h3>
              <p className="text-sm text-gray-600 mb-4">
                O contrato <strong>{contratoGerado?.numero_contrato}</strong> foi criado e está pronto para edição.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-700">
                  <p><strong>Número:</strong> {contratoGerado?.numero_contrato}</p>
                  <p><strong>Tipo:</strong> {contratoGerado?.tipo_contrato}</p>
                  <p><strong>Status:</strong> {contratoGerado?.status}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Você pode editar o contrato agora ou fechá-lo e editá-lo mais tarde na lista de contratos.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t">
          {step === 'form' ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancelar
              </button>
              <button
                onClick={handleGerarContrato}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Gerar Contrato
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Fechar
              </button>
              <button
                onClick={handleEditarAgora}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                Editar Agora
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )

  // Use portal to render modal outside component tree
  return createPortal(modalContent, document.body)
}

export default ModalContratos
